<template>
  <v-list-item :ref="`filterLine${index}`">
    <v-list-item-content>
      <v-list-item-title>
        <v-row align="baseline">
          <v-col cols="3">
            <v-autocomplete
              v-model="localColumn"
              :items="columnItems"
              :label="$t('column')"
              append-icon="mdi-chevron-down"
              :prepend-icon="`mdi-${getIcon(localColumn?.type)}`"
              return-object
              :loading="isLoading"
              item-title="text"
              :filter="customFilter"
              style="align-items: baseline"
              :rules="[(value) => required(value) || 'This field is required']"
            >
              <template v-slot:item="{ on, props, item }">
                <v-list-item v-bind="props" :subtitle="item.type" v-on="on">
                  <v-list-item-icon class="my-auto">
                    <v-icon>mdi-{{ getIcon(item.type) }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>
                    {{ item.text }}
                  </v-list-item-title>
                </v-list-item>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="3">
            <v-select
              v-model="localOperator"
              :items="availableOperators"
              :label="$t('operator')"
              append-icon="mdi-chevron-down"
              :loading="isLoading"
              :rules="[(value) => !!value?.length || 'This field is required']"
            ></v-select>
          </v-col>

          <v-col cols="6">
            <!-- Range Input -->
            <v-combobox
              v-model="localValue"
              v-if="localOperator === 'R'"
              :rules="[(value) => required(value) || 'This field is required']"
              :disabled="isLoading"
              :label="isNumber ? $t('number') : $t('text')"
              :hint="$t('enterValues')"
              :delimiters="[',', ';', ' ']"
              multiple
              small-chips
              @input="localValueArrayToInt"
            ></v-combobox>

            <!-- Number Input -->
            <v-text-field
              v-model.number="localValue"
              :disabled="isLoading"
              v-else-if="isNumber"
              :rules="[
                (value) => required(value) || 'Please fill in a valid number',
                (value) => !isNaN(+value) || 'Please fill in a valid number',
              ]"
              :label="$t('number')"
              :hint="$t('enterANumber')"
              type="number"
            ></v-text-field>

            <!-- Date Input -->
            <v-row v-else-if="isDate" align="baseline">
              <v-col v-if="relativeDate" class="d-flex">
                <v-text-field
                  :label="$t('dateAgo')"
                  :value="-localValue.period"
                  @input="localValue = { ...localValue, period: -$event }"
                  :disabled="isLoading"
                  type="number"
                  min="0"
                  max="1461"
                  hide-details
                  class="mr-2 flex-grow-1"
                  :rules="[
                    (value) =>
                      required(value) || 'Please fill in a valid number',
                    (value) => value > 0 || 'Please fill in a valid number',
                  ]"
                ></v-text-field>
                <v-select
                  :value="localValue.unit"
                  @input="localValue = { ...localValue, unit: $event }"
                  :disabled="isLoading"
                  :items="[
                    { text: $t('days'), value: 'days' },
                    { text: $t('months'), value: 'months' },
                    { text: $t('years'), value: 'years' },
                  ]"
                  hide-details
                  style="max-width: 120px"
                  :rules="[
                    (value) => !!value?.length || 'This field is required',
                  ]"
                ></v-select>
              </v-col>

              <v-menu
                v-else
                v-model="menuTabValueDate"
                :disabled="isLoading"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="localValue"
                    :disabled="isLoading"
                    class="mt-4"
                    readonly
                    dense
                    :label="$t('date')"
                    v-bind="attrs"
                    v-on="on"
                    append-icon="mdi-calendar-blank"
                    :rules="[
                      (value) => !!value?.length || 'This field is required',
                    ]"
                  ></v-text-field>
                </template>

                <v-date-picker
                  v-model="localValue"
                  :disabled="isLoading"
                  @input="menuTabValueDate = false"
                ></v-date-picker>
              </v-menu>

              <v-switch
                v-model="relativeDate"
                :disabled="isLoading"
                :label="$t('relativeDate')"
                class="mx-6"
              ></v-switch>
            </v-row>

            <!-- Boolean Input -->
            <v-select
              v-model="localValue"
              :disabled="isLoading"
              v-else-if="isBoolean"
              :items="[
                { text: $t('true'), value: true },
                { text: $t('false'), value: false },
              ]"
              :label="$t('value')"
              append-icon="mdi-chevron-down"
              :rules="[
                (value) =>
                  value === true || value === false || 'This field is required',
              ]"
            ></v-select>

            <!-- Free Input -->
            <v-text-field
              v-model="localValue"
              :disabled="isLoading"
              v-else
              :label="$t('text')"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-list-item-title>
    </v-list-item-content>
    <v-list-item-action>
      <v-btn text icon color="error" @click="$emit('delete')">
        <v-icon>mdi-delete</v-icon>
      </v-btn>
    </v-list-item-action>
  </v-list-item>
</template>

<script>
import { required, email, minLength, maxLength } from 'vuelidate/lib/validators'
import {
  isNumber,
  isText,
  isDate,
  isBoolean,
} from '@/utils/supported-export-types.js'
import { getIcon } from '@/utils/supported-export-types.js'

export default {
  name: 'FilterConditionRow',
  props: {
    value: {
      type: [Object, String, Number],
      default: '',
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    columnItems: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      menuTabValueDate: false,
      relativeDate: null,
    }
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (!isDate(newValue.column.type)) return

        const isRelativeDate =
          typeof newValue.value === 'object' &&
          newValue.value.type === 'relative_date'

        if (isRelativeDate && !this.relativeDate) this.relativeDate = true

        if (
          !isRelativeDate &&
          (isNaN(new Date(newValue.value)) || !isNaN(newValue.value))
        )
          this.localValue = this.getDefaultValue()
      },
    },
    relativeDate: {
      handler(newValue) {
        if (newValue) {
          this.localValue = { period: -7, unit: 'days', type: 'relative_date' }
        } else {
          this.localValue = new Date().toISOString().split('T')[0]
        }
      },
    },
  },
  computed: {
    localColumn: {
      get() {
        return this.value.column
      },
      set(newColumn) {
        if (newColumn) {
          this.$emit('input', {
            ...this.value,
            column: newColumn,
            operator: this.getDefaultOperator(newColumn.type),
            value: this.getDefaultValue(newColumn.type),
          })
        }
      },
    },
    localOperator: {
      get() {
        return this.value.operator
      },
      set(newOperator) {
        this.$emit('input', { ...this.value, operator: newOperator })
      },
    },
    localValue: {
      get() {
        return this.value.value
      },
      set(newValue) {
        this.$emit('input', { ...this.value, value: newValue })
      },
    },
    isLoading() {
      return !this.columnItems?.length
    },
    isNumber() {
      return isNumber(this.value.column.type)
    },
    isText() {
      return isText(this.value.column.type)
    },
    isDate() {
      return isDate(this.value.column.type)
    },
    isBoolean() {
      return isBoolean(this.value.column.type)
    },
    availableOperators() {
      const allOperators = [
        { text: '=', value: 'E' }, // Equal
        { text: '>', value: 'G' }, // Greater than
        { text: '<', value: 'L' }, // Lower than
        { text: '≥', value: 'g' },
        { text: '≤', value: 'l' },
        { text: '≠', value: 'N' }, // Not equal
        { text: this.$t('contains'), value: 'i' }, // In
        { text: this.$t('notContains'), value: 'o' }, // Out
        { text: this.$t('in'), value: 'R' }, // Range
      ]
      let availables = 'EGLglNioR'
      if (this.isDate || this.isNumber || this.isBoolean) {
        availables = availables.replace('io', '')
      }
      if (this.isText || this.isBoolean) {
        availables = availables.replace('GLgl', '')
      }
      if (this.isDate || this.isBoolean) {
        availables = availables.replace('R', '')
      }

      return allOperators.filter(({ value }) => availables.includes(value))
    },
    valueInputComponent() {
      if (this.isNumber || this.isText) return 'v-text-field'
      if (this.isDate) return 'v-menu'
      return 'v-select'
    },
    valueInputProps() {
      if (this.isNumber) {
        return {
          rules: [
            (value) => !isNaN(value) || 'Veuillez entrer un nombre valide',
          ],
          label: this.$t('value'),
          hint: 'Entrez un nombre',
        }
      }
      if (this.isText) {
        return {
          label: this.$t('value'),
        }
      }
      if (this.isDate) {
        return {
          closeOnContentClick: false,
          nudgeRight: 40,
          transition: 'scale-transition',
          offsetY: true,
          minWidth: 'auto',
          activator: { on: {}, attrs: {} },
          picker: {
            value: this.localValue,
            events: {
              input: (val) => {
                this.menuTabValueDate = false
                this.localValue = val
              },
            },
          },
        }
      }
      return {
        items: [
          { text: 'True', value: true },
          { text: 'False', value: false },
        ],
        label: this.$t('value'),
        appendIcon: 'mdi-chevron-down',
      }
    },
  },
  methods: {
    getIcon,
    required,
    email,
    minLength,
    maxLength,
    customFilter(item, queryText) {
      const searchTerm = queryText.toLowerCase()
      return (
        item.text.toLowerCase().includes(searchTerm) ||
        item.type.toLowerCase().includes(searchTerm)
      )
    },
    getDefaultOperator() {
      return this.isText ? 'i' : 'E'
    },
    getDefaultValue() {
      if (this.isNumber) return 0
      if (this.isDate) return new Date().toISOString().split('T')[0]
      if (this.isBoolean) return false
      return ''
    },
    localValueArrayToInt(event) {
      if (this.isNumber) {
        this.localValue = event
          .map((value) => parseInt(value, 10))
          .filter((value) => !isNaN(value))
      }
    },
  },
}
</script>
